import { BridgeError, HttpBridge, HttpEventsChannel } from '@kinoq/camera-protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { BridgeCamera } from './bridgeCameras';
import type { CameraSource } from './cameraSource';
import { bridgeUrl } from './desktopDiscovery';

interface BridgeCameraSource extends CameraSource {
  /** Object URL of the latest preview frame. */
  frameUrl: string | undefined;
}

interface Session {
  bridge: HttpBridge;
  /** Captures run one at a time: a bridge holds one camera session and serves one still. */
  captures: Promise<unknown>;
}

// Sessions on one bridge take turns. A release still in flight would otherwise free the
// camera the next session (a StrictMode remount, a quick switch back) has just bound.
const released = new Map<string, Promise<void>>();

/**
 * A camera on a KinoQ bridge, live while `camera` is set: connects, streams preview frames,
 * and treats the bridge's trigger (a shutter pressed on the phone) as `onTrigger`.
 */
export function useBridgeCamera(
  camera: BridgeCamera | null,
  onTrigger: () => void,
): BridgeCameraSource {
  const [frameUrl, setFrameUrl] = useState<string>();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const session = useRef<Session | null>(null);
  const triggerRef = useRef(onTrigger);
  useEffect(() => {
    triggerRef.current = onTrigger;
  });

  const bridgeId = camera?.bridgeId;
  const cameraId = camera?.cameraId;
  useEffect(() => {
    if (!bridgeId || !cameraId) return;
    const bridge = new HttpBridge(bridgeUrl(bridgeId));
    const abort = new AbortController();
    let url: string | undefined;
    let unsubscribe = () => {};
    const show = (next: string | undefined) => {
      if (url) URL.revokeObjectURL(url);
      url = next;
      setFrameUrl(next);
    };
    setError('');
    const connected = (released.get(bridgeId) ?? Promise.resolve()).then(async () => {
      if (abort.signal.aborted) return false;
      await bridge.connect(cameraId);
      return true;
    });
    connected
      .then(async (live) => {
        if (!live || abort.signal.aborted) return;
        session.current = { bridge, captures: Promise.resolve() };
        setReady(true);
        unsubscribe = new HttpEventsChannel(bridgeUrl(bridgeId)).subscribeEvents(
          (event) => {
            if (event.type === 'trigger') triggerRef.current();
          },
          () => {},
        );
        for await (const frame of bridge.startPreview(abort.signal)) {
          if (abort.signal.aborted) return;
          show(URL.createObjectURL(frame.data));
        }
        throw new BridgeError('PreviewLost', 'the bridge ended the preview');
      })
      .catch((e: unknown) => {
        if (abort.signal.aborted) return;
        setReady(false);
        setError(bridgeErrorMessage(e));
      });
    return () => {
      abort.abort();
      unsubscribe();
      session.current = null;
      show(undefined);
      setReady(false);
      // Released only once connect settles, so the release cannot reach the bridge first.
      // A refused connect is released too: the bridge may have bound the camera anyway.
      const release = connected
        .then(
          (live) => (live ? bridge.disconnect() : undefined),
          () => bridge.disconnect(),
        )
        .catch(() => {});
      released.set(bridgeId, release);
    };
  }, [bridgeId, cameraId]);

  const capture = useCallback(async () => {
    const current = session.current;
    if (!current) throw new Error('The camera is not connected yet.');
    const shot = current.captures.then(() => current.bridge.capture());
    current.captures = shot.catch(() => {});
    try {
      return (await shot).blob;
    } catch (e) {
      throw new Error(bridgeErrorMessage(e));
    }
  }, []);

  return { ready, error, frameUrl, capture };
}

function bridgeErrorMessage(e: unknown): string {
  const code = e instanceof BridgeError ? e.code : undefined;
  if (code === 'PreviewLost')
    return 'Lost the camera preview. Switch cameras or choose from the gallery.';
  if (code === 'Timeout')
    return 'The camera stopped answering. Switch cameras or choose from the gallery.';
  if (code === 'CaptureFailed') return 'The camera could not take the photo.';
  return 'The camera is not reachable. Switch cameras or choose from the gallery.';
}
